import React from 'react';
import { highlightInlineText } from './highlightInlineText';

/**
 * Processes text to handle both highlighting (*text*) and markdown-style links [text](url).
 * @param {string} text - The text to process
 * @returns {import('react').ReactNode[] | null}
 */
export function processTextWithLinks(text) {
  if (!text) return null;

  // First, process highlighting to get React elements
  const highlightedParts = highlightInlineText(text, 'orange');

  // If highlighting didn't create any React elements, process for links
  if (typeof highlightedParts === 'string') {
    return processLinks(highlightedParts);
  }

  // If we have React elements, we need to process links within each text part
  if (highlightedParts) {
    return highlightedParts.map((part, i) => {
      if (typeof part === 'string') {
        return processLinks(part, i);
      }
      // If it's a React element (highlighted span), check if it contains links
      if (React.isValidElement(part)) {
        const children = part.props.children;
        if (typeof children === 'string') {
          const linkProcessed = processLinks(children, i);
          // If links were found, replace the span's children
          if (linkProcessed.some((item) => React.isValidElement(item))) {
            return React.cloneElement(part, {}, linkProcessed);
          }
        }
      }
      return part; // Already a React element (highlighted span)
    });
  }

  return null;
}

/**
 * Processes text for markdown-style links [text](url).
 * @param {string} text - The text to process
 * @param {number} baseKey - Base key for React keys
 * @returns {import('react').ReactNode[]}
 */
function processLinks(text, baseKey = 0) {
  const linkRegex = /(\[[^\]]+\]\([^)]+\))/g;
  const parts = text.split(linkRegex);

  return parts.map((part, i) => {
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (linkMatch) {
      const [, linkText, linkUrl] = linkMatch;
      return (
        <a
          key={`${baseKey}-link-${i}`}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'underline' }}
        >
          {linkText}
        </a>
      );
    }
    return part;
  });
}
